<template>
    <b-form-group
        :valid-feedback="$attrs['valid-feedback']"
        :invalid-feedback="$attrs['invalid-feedback']"
        :state="$attrs.state"
    >
        <template #label>
            <label class="label" :for="id">
                {{ $tc('label', pluralization) }}
            </label>
            <PopoverHelp v-if="showHelp" :id="popoverHelpId" :html="popoverHelp" />
        </template>
        <b-input-group :id="popoverCapsLockId">
            <b-form-input
                :id="id"
                class="input"
                v-bind="$attrs"
                v-on="$listeners"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$tc('placeholder', pluralization)"
            />
            <b-input-group-prepend>
                <b-button variant="light" @click="onClickTogglePassword">
                    <i class="fa fa-eye" v-if="!showPassword" />
                    <i class="fa fa-eye-slash" v-else-if="showPassword" />
                </b-button>
            </b-input-group-prepend>
        </b-input-group>
        <PopoverCapsLock :target="popoverCapsLockId" :show="showCapsLockWarning" />
    </b-form-group>
</template>

<script>
import PopoverHelp from './PopoverHelp'
import PopoverCapsLock from './PopoverCapsLock'

export default {
    name: 'InputPassword',
    components: { PopoverHelp, PopoverCapsLock },
    inheritAttrs: false,
    props: {
        password: {
            type: Boolean,
            required: false,
            default: false,
        },
        newPassword: {
            type: Boolean,
            required: false,
            default: false,
        },
        confirmNewPassword: {
            type: Boolean,
            required: false,
            default: false,
        },
        showHelp: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            showPassword: false,
            showCapsLockWarning: false,
            capsLockWarningTimeout: null,
        }
    },
    computed: {
        id() {
            if (this.password) return 'password'
            if (this.newPassword) return 'new-password'
            if (this.confirmNewPassword) return 'confirm-new-password'
            return 'password'
        },
        popoverHelpId() {
            if (this.password) return 'popover-help-password'
            if (this.newPassword) return 'popover-help-new-password'
            if (this.confirmNewPassword) return 'popover-help-confirm-new-password'
            return 'popover-help-password'
        },
        popoverCapsLockId() {
            if (this.password) return 'popover-caps-lock-password'
            if (this.newPassword) return 'popover-caps-lock-new-password'
            if (this.confirmNewPassword) return 'popover-caps-lock-confirm-new-password'
            return 'popover-caps-lock-password'
        },
        pluralization() {
            if (this.password) return 0
            if (this.newPassword) return 1
            if (this.confirmNewPassword) return 2
            return 0
        },
        popoverHelp() {
            if (this.password) return [this.$t('popoverPassword')]
            if (this.newPassword) return [this.$t('popoverNewPassword')]
            if (this.confirmNewPassword) return [this.$t('popoverConfirmNewPassword')]
            return [this.$t('popoverPassword')]
        },
    },
    created() {
        document.addEventListener('keyup', (e) => this.capsLockCallback(e))
    },
    destroyed() {
        document.removeEventListener('keyup', (e) => this.capsLockCallback(e))
    },
    methods: {
        onClickTogglePassword() {
            this.showPassword = !this.showPassword
        },
        capsLockCallback(e) {
            const key = e.key
            if (key === 'CapsLock') {
                if (e.getModifierState(key)) {
                    this.showCapsLockWarning = true
                    this.capsLockWarningTimeout = setTimeout(() => {
                        this.showCapsLockWarning = false
                    }, 3500)
                } else {
                    this.showCapsLockWarning = false
                    clearTimeout(this.capsLockWarningTimeout)
                }
            }
        },
    },
    i18n: {
        messages: {
            'en-US': {
                label: 'Password | New password | Confirm new password',
                placeholder: 'Enter your password | Enter your new password | Re-enter your new password',
                popoverPassword:
                    'Provide the password you registered in the app for the user above.<br/>' +
                    'If you don\'t remember your password, you can use the <i>"Forgot password?"</i> option or send an email to <b>suporte@coblue.com.br</b>.',
                popoverNewPassword:
                    'The new password must contain:<br/>' +
                    'Between 12 and 64 alphanumeric characters (letters and numbers)<br/>' +
                    'Uppercase and lowercase letters<br/>' +
                    'At least 1 special character (!@# etc)',
                popoverConfirmNewPassword: 'Just re-enter the same password.',
            },
            'es-ES': {
                label: 'Contraseña | Nueva contraseña | Confirmar nueva contraseña',
                placeholder:
                    'Ingrese su contraseña | Ingrese su nueva contraseña | Vuelva a ingresar su nueva contraseña',
                popoverPassword:
                    'Proporcione la contraseña que registró en el app para el usuario anterior.<br/>' +
                    'Si no recuerdas tu contraseña, puedes usar la opción <i>"¿Olvidaste la contraseña?"</i> o envíe un correo electrónico a <b>suporte@coblue.com.br</b>.',
                popoverNewPassword:
                    'La nueva contraseña debe contener:<br/>' +
                    'Entre 12 y 64 caracteres alfanuméricos (letras y números)<br/>' +
                    'Letras mayúsculas y minúsculas<br/>' +
                    'Al menos 1 carácter especial (!@# etc)',
                popoverConfirmNewPassword: 'Simplemente vuelve a introducir la misma contraseña.',
            },
            'pt-BR': {
                label: 'Senha | Nova senha | Confirme sua nova senha',
                placeholder: 'Insira sua senha | Insira sua nova senha | Insira novamente sua nova senha',
                popoverPassword:
                    'Informe a senha que você cadastrou no app para o usuário acima.<br/>' +
                    'Caso não se lembre da sua senha, você pode usar a opção <i>"Esqueceu a senha?"</i> ou envie um email para <b>suporte@coblue.com.br</b>.',
                popoverNewPassword:
                    'A nova senha deve conter:<br/>' +
                    'Entre 12 e 64 caracteres alfanuméricos (letras e números)<br/>' +
                    'Letras maiúsculas e minúsculas<br/>' +
                    'Pelo menos 1 caractere especial (!@# etc)',
                popoverConfirmNewPassword: 'Apenas insira a mesma senha novamente.',
            },
        },
    },
}
</script>
